import { createSelector } from 'reselect';
import { denormalize } from 'normalizr';
import * as schemas from '../schemas';
import {
  createDeepEqualSelector,
  createPropertyEqualSelector
} from '../util/selectors';
import { isReservedName } from '../util/username';

// Only display pushbot comments after July 11, 2018
// following deployment of @catalytic/pushbot-api#5397
export const PUSHBOT_COMMENTS_DATE = new Date(2018, 6, 11);

export const entities = ({ entities = {} } = {}) => entities;

// Helpers
const toList = obj => Object.keys(obj).map(k => obj[k]);
const getTree = (name, tree = entities) =>
  createPropertyEqualSelector(name)(tree, (tree = {}) => tree);
const getType = (name, tree = entities) =>
  createSelector(tree, (tree = {}) => tree[name]);
const getTypeAsList = selector =>
  createDeepEqualSelector(
    selector,
    results => (!results ? results : toList(results))
  );
const getTypeDeepAsList = (selector, schema, tree = entities) => {
  return createSelector(tree, selector, (tree = {}, entities) => {
    return !entities
      ? entities
      : entities.map(entity => denormalize(entity, schema, tree));
  });
};
const getTypeDeep = (selector, id = 'id') =>
  createSelector(
    selector,
    results => (!results ? results : schemas.mapEntitiesByID(results, id))
  );

export const createTransformSelector = (
  id,
  type,
  typeList,
  typeDeep,
  typeDeepList
) => transform => {
  const select = createSelector(
    type,
    values =>
      !values ? values : schemas.mapEntitiesByID(transform(toList(values)), id)
  );
  const selectList = createSelector(
    typeList,
    values => (!values ? values : transform(values))
  );
  const selectDeep = createSelector(
    typeDeep,
    values =>
      !values ? values : schemas.mapEntitiesByID(transform(toList(values)), id)
  );
  const selectDeepList = createSelector(
    typeDeepList,
    values => (!values ? values : transform(values))
  );
  return [
    select,
    selectList,
    selectDeep,
    selectDeepList,
    createTransformSelector(id, select, selectList, selectDeep, selectDeepList)
  ];
};

export const createTypeSelector = (schema, tree = entities) => {
  const name = schema.key;
  const id = schema.getIdAttribute();
  const typeTree = getTree(name, tree);
  const type = getType(name, typeTree);
  const typeList = getTypeAsList(type, id);
  const typeDeepList = getTypeDeepAsList(typeList, schema, typeTree, id);
  const typeDeep = getTypeDeep(typeDeepList, id);
  return [
    type,
    typeList,
    typeDeep,
    typeDeepList,
    createTransformSelector(id, type, typeList, typeDeep, typeDeepList)
  ];
};

export const [
  apps,
  appsList,
  appsDeep,
  appsListDeep,
  createAppsTransformSelector
] = createTypeSelector(schemas.APP);

export const [
  contacts,
  contactsList,
  contactsDeep,
  contactsListDeep,
  createContactsTransformSelector
] = createTypeSelector(schemas.CONTACT);

export const [
  fields,
  fieldsList,
  fieldsDeep,
  fieldsListDeep,
  createFieldsTransformSelector
] = createTypeSelector(schemas.FIELD);

export const [
  groups,
  groupsList,
  groupsDeep,
  groupsListDeep,
  createGroupsTransformSelector
] = createTypeSelector(schemas.GROUP);

export const [
  integrations,
  integrationsList,
  integrationsDeep,
  integrationsListDeep,
  createIntegrationsTransformSelector
] = createTypeSelector(schemas.INTEGRATION);

export const [
  invites,
  invitesList,
  invitesDeep,
  invitesListDeep,
  createInvitesTransformSelector
] = createTypeSelector(schemas.INVITE);

export const [
  messages,
  messagesList,
  messagesDeep,
  messagesListDeep,
  createMessagesTransformSelector
] = createTypeSelector(schemas.MESSAGE);

export const [
  comments,
  commentsList,
  commentsDeep,
  commentsListDeep,
  createCommentsTransformSelector
] = createMessagesTransformSelector(entities =>
  entities.filter(
    ({ username, timestamp }) =>
      !isReservedName(username) ||
      username === 'public' ||
      (username === 'pushbot' && new Date(timestamp) > PUSHBOT_COMMENTS_DATE)
  )
);

export const [
  notifications,
  notificationsList,
  notificationsDeep,
  notificationsListDeep,
  createNotificationsTransformSelector
] = createTypeSelector(schemas.NOTIFICATION);

export const [
  predictors,
  predictorsList,
  predictorsDeep,
  predictorsListDeep,
  createPredictorsTransformSelector
] = createTypeSelector(schemas.PREDICTOR);

export const [
  processes,
  processesList,
  processesDeep,
  processesListDeep,
  createProcessesTransformSelector
] = createTypeSelector(schemas.PROCESS);

export const [
  runBase,
  runBaseList,
  runBaseDeep,
  runBaseListDeep,
  createRunBaseTransformSelector
] = createTypeSelector(schemas.RUN);

export const [
  runs,
  runsList,
  runsDeep,
  runsListDeep,
  createRunsTransformSelector
] = createRunBaseTransformSelector(entities =>
  entities.map(entity => {
    const { threadID = {} } = entity;

    if (typeof threadID !== 'object') {
      return {
        commentCount: 0,
        ...entity
      };
    }

    const { messages = [] } = threadID;
    const commentCount = messages.filter(
      ({ username }) => !isReservedName(username)
    ).length;
    return {
      commentCount,
      ...entity
    };
  })
);

export const [
  tasks,
  tasksList,
  tasksDeep,
  tasksListDeep,
  createTasksTransformSelector
] = createTypeSelector(schemas.TASK);

export const [
  steps,
  stepsList,
  stepsDeep,
  stepsListDeep,
  createStepTransformSelector
] = createTypeSelector(schemas.STEP);

export const [
  tables,
  tablesList,
  tablesDeep,
  tablesListDeep,
  createTablesTransformSelector
] = createTypeSelector(schemas.TABLE);

export const [
  tableRows,
  tableRowsList,
  tableRowsDeep,
  tableRowsListDeep,
  createTableRowsTransformSelector
] = createTypeSelector(schemas.TABLE_ROW);

export const [
  teams,
  teamsList,
  teamsDeep,
  teamsListDeep,
  createTeamsTransformSelector
] = createTypeSelector(schemas.TEAM);

export const [
  threads,
  threadsList,
  threadsDeep,
  threadsListDeep,
  createThreadsTransformSelector
] = createTypeSelector(schemas.THREAD);

export const [
  triggers,
  triggersList,
  triggersDeep,
  triggersListDeep,
  createTriggersTransformSelector
] = createTypeSelector(schemas.TRIGGER);

export const [
  users,
  usersList,
  usersDeep,
  usersListDeep,
  createUsersTransformSelector
] = createTypeSelector(schemas.USER);

export const [
  webhooks,
  webhooksList,
  webhooksDeep,
  webhooksListDeep,
  createWebhooksTransformSelector
] = createTypeSelector(schemas.WEBHOOK);

export const [
  files,
  filesList,
  filesDeep,
  fileListDeep,
  createFilesTransformSelector
] = createTypeSelector(schemas.FILE);

export const [
  visibleFields,
  visibleFieldsList,
  visibleFieldsDeep,
  visibleFieldsListDeep,
  createVisibleFieldsTransformSelector
] = createTypeSelector(schemas.VISIBLE_FIELD);

export const [
  filters,
  filtersList,
  filtersDeep,
  filtersListDeep,
  createFiltersTransformSelector
] = createTypeSelector(schemas.FILTER);

// TODO this will be deprecated after https://github.com/catalyticlabs/pushbot-api/pull/6643
export const [
  credentials,
  credentialsList,
  credentialsDeep,
  credentialsListDeep,
  createCredentialsTransformSelector
] = createTypeSelector(schemas.CREDENTIAL);

export const searchesDeep = createSelector(
  processesDeep,
  runsDeep,
  tablesDeep,
  tasksDeep,
  (processes, runs, tables, tasks) => ({ processes, runs, tables, tasks })
);



// WEBPACK FOOTER //
// ./js/selectors/entities.js